// src/resources/customerLifecycle/api.ts
//
// Chamadas custom da tela "Ciclo de vida da base" (plano
// docs/superpowers/plans/2026-09-29-recuperacao-clientes-campanha.md, Task 10).
//
// `/customer-lifecycle/*` usa parâmetros de query PLANOS (chainId, atRiskDays,
// inactiveDays, tagIds, segment, skip, take, order), diferente do resto do admin
// (que embrulha tudo em `filter={...}` via src/store/providers/getAll.js). O
// backend faz `JSON.parse` de cada valor de query com fallback pra string crua
// (src/server.ts do backend), então tanto `chainId="<uuid>"` (com aspas, igual
// ao resto do app) quanto `atRiskDays=60` (sem aspas) chegam certos — por isso
// as chamadas aqui vão direto por `httpClientReactAdmin` + URLSearchParams em
// vez do dataProvider genérico (que forçaria o formato `filter=`).
import { DEFAULT_API_ADMIN } from '../../constants';
import { httpClientReactAdmin } from '../../lib';

export type LifecycleSegment = 'active' | 'at_risk' | 'inactive' | 'never';

export interface LifecycleSummary {
  total: number;
  active: number;
  atRisk: number;
  inactive: number;
  neverFueled: number;
  thresholds: { atRiskDays: number; inactiveDays: number };
  chainDefaults: { atRiskDays: number; inactiveDays: number };
}

export interface LifecycleCustomer {
  id: string;
  name: string;
  cpf: string | null;
  cellphone: string | null;
  tags: Array<{ id: string; name: string; color?: string | null }>;
  lastFillinAt: string | null;
  daysWithoutFueling: number | null;
  createdAt: string;
  activeCampaign: { id: string; name: string } | null;
}

export interface LifecycleFilters {
  chainId: string;
  atRiskDays: number | null;
  inactiveDays: number | null;
  tagIds: string[];
}

const buildBaseParams = ({ chainId, atRiskDays, inactiveDays, tagIds }: LifecycleFilters) => {
  const params = new URLSearchParams();
  params.set('chainId', JSON.stringify(chainId));
  if (atRiskDays != null) params.set('atRiskDays', String(atRiskDays));
  if (inactiveDays != null) params.set('inactiveDays', String(inactiveDays));
  if (tagIds.length) params.set('tagIds', JSON.stringify(tagIds));
  return params;
};

export const fetchLifecycleSummary = async (filters: LifecycleFilters): Promise<LifecycleSummary> => {
  const params = buildBaseParams(filters);
  const url = `${DEFAULT_API_ADMIN}/customer-lifecycle/summary?${params.toString()}`;
  const { json } = await httpClientReactAdmin(url, { method: 'GET' });
  return (json?.data ?? json) as LifecycleSummary;
};

export interface FetchCustomersOptions extends LifecycleFilters {
  segment: LifecycleSegment;
  skip: number;
  take: number;
  order: { field: 'lastFillinAt' | 'name' | 'createdAt'; direction: 'ASC' | 'DESC' };
}

export const fetchLifecycleCustomers = async (
  opts: FetchCustomersOptions,
): Promise<{ data: LifecycleCustomer[]; total: number }> => {
  const params = buildBaseParams(opts);
  params.set('segment', JSON.stringify(opts.segment));
  params.set('skip', String(opts.skip));
  params.set('take', String(opts.take));
  params.set('order', JSON.stringify({ [opts.order.field]: opts.order.direction }));
  const url = `${DEFAULT_API_ADMIN}/customer-lifecycle/customers?${params.toString()}`;
  const { json } = await httpClientReactAdmin(url, { method: 'GET' });
  return { data: (json?.data ?? []) as LifecycleCustomer[], total: Number(json?.total ?? 0) };
};

export const saveLifecycleThresholds = async (
  chainId: string,
  atRiskDays: number,
  inactiveDays: number,
): Promise<{ atRiskDays: number; inactiveDays: number }> => {
  const url = `${DEFAULT_API_ADMIN}/customer-lifecycle/thresholds`;
  const { json } = await httpClientReactAdmin(url, {
    method: 'PUT',
    body: JSON.stringify({ chainId, atRiskDays, inactiveDays }),
  });
  return (json?.data ?? json) as { atRiskDays: number; inactiveDays: number };
};
